// External libraries
import * as React from "react";
import { observer } from "mobx-react";
import { useDialog } from "@shared-ui/dialog-context";
import { UitkFigure, UitkFigureAspectRatioType, UitkImage } from "uitk-react-images";
import { classNames } from "uitk-react-utils";
import { UitkLoader } from "uitk-react-loader";
import { withStores } from "stores";

// Components
import { ModuleMapProps } from "../typings";
import { useFullscreenOpen } from "../hooks";
import { MapContext } from "../MapContext";
import { MapsFlexModuleResult } from "typings/microserviceModels/maps-flex-module";

const ModuleMap = React.lazy(() => import(/* webpackChunkName: "module-map" */ "../ModuleMap"));

const isInViewport = (element: HTMLDivElement): boolean => {
  const distance = element.getBoundingClientRect();
  if (!distance) {
    return false;
  }

  return (
    distance.bottom > (window.innerHeight || document.documentElement.clientHeight) &&
    distance.top < (window.innerHeight || document.documentElement.clientHeight)
  );
};

export const DynamicMaps = withStores(
  "staticMap",
  "context"
)(
  observer((props: ModuleMapProps) => {
    const { staticMap, flexTemplateNode, context, flexModuleModelStore } = props;
    const { id, options } = flexTemplateNode;
    const [isDialogOpen, dialogActions] = useDialog(`open-${id}`);
    const { sendUserToSRP, toggleShowFullscreenMap } = useFullscreenOpen(props, isDialogOpen, dialogActions);
    const model = flexModuleModelStore.getModel(id) as MapsFlexModuleResult | null;
    if (!model) {
      return null;
    }
    const shouldOpenMapInSRP = false;
    const countryCode = (context && context.site && context.site.country) || "";
    const elemRef = React.useRef<HTMLDivElement | null>(null);
    const placeHolderAltText =
      (context &&
        context.searchContext &&
        context.searchContext.location &&
        context.searchContext.location.extendedName) ||
      "";

    if (typeof window === "undefined") {
      staticMap.generateGooglePlaceHolderMapImage(context, countryCode);
    }

    const [isLoaderVisible, showLoader] = React.useState(true);

    const [isBottomSheetSticky, setBottomSheetPosition] = React.useState(true);

    const [isClient, setIsClient] = React.useState(false);

    React.useEffect(() => {
      setIsClient(true);
    }, []);

    // Handler anchoring bottom sheet to the bottom of the window
    React.useEffect(() => {
      const viewportSizeCheck = () => {
        setBottomSheetPosition(isInViewport(elemRef.current as HTMLDivElement));
      };
      window.addEventListener("scroll", viewportSizeCheck);

      return () => window.removeEventListener("scroll", viewportSizeCheck);
    }, []);

    const toggleLoader = () => {
      showLoader(!isLoaderVisible);
    };

    return (
      <div
        className="Maps uitk-flex uitk-cell all-cell-1-1 all-y-padding-three"
        id={id}
        data-fm={options.fmId}
        data-fm-title-id={options.fmTitleId}
      >
        <UitkFigure ratio={UitkFigureAspectRatioType.R21_9}>
          {staticMap && staticMap.signedPlaceHolderUrl && (
            <UitkImage alt={placeHolderAltText} src={staticMap.signedPlaceHolderUrl} data-testid="placeholder-map" />
          )}
          {isLoaderVisible && <UitkLoader className="dynamicMapLoader" />}
          <div ref={elemRef} className={classNames("uitk-flex dynamicMap")} data-testid="dynamic-map">
            <MapContext.Provider
              value={{
                shouldOpenMapInSRP,
                sendUserToSRP,
                toggleShowFullscreenMap,
              }}
            >
              {isClient && (
                <React.Suspense fallback={<UitkLoader className="dynamicMapLoader" />}>
                  <ModuleMap
                    {...props}
                    classNames={classNames(
                      "uitk-dialog-content uitk-dynamic-map uitk-cell uitk-grid",
                      isBottomSheetSticky && "setStickyBottomSheet"
                    )}
                    onMapMounted={toggleLoader}
                    isDynamic
                  />
                </React.Suspense>
              )}
            </MapContext.Provider>
          </div>
        </UitkFigure>
      </div>
    );
  })
);

DynamicMaps.displayName = "DynamicMaps";

export default DynamicMaps;
